import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, message, Space } from 'antd';
import { getAuthSettings, updateAuthSettings, logout, getTwoFaSettings, updateTwoFaSettings } from '../services/api';
import { DEFAULT_TWO_FA_URL, getTwoFaUrl, setTwoFaUrlCache } from '../constants/mappings';

const SystemSettings = ({ onUpdated }) => {
  const [authForm] = Form.useForm();
  const [twoFaForm] = Form.useForm();
  const [savingTwoFa, setSavingTwoFa] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAuthSettings();
        authForm.setFieldsValue({ username: data.username });
      } catch (e) {
        // ignore
      }
    })();

    (async () => {
      try {
        const data = await getTwoFaSettings();
        const url = (data?.two_fa_url && String(data.two_fa_url).trim()) || getTwoFaUrl();
        setTwoFaUrlCache(url);
        twoFaForm.setFieldsValue({ two_fa_url: url });
      } catch (e) {
        twoFaForm.setFieldsValue({ two_fa_url: getTwoFaUrl() });
      }
    })();
  }, [authForm, twoFaForm]);

  const onAuthFinish = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的新密码不一致');
      return;
    }
    try {
      await updateAuthSettings({
        currentPassword: values.currentPassword,
        username: values.username,
        newPassword: values.newPassword
      });
      message.success('已更新，请重新登录');
      try { await logout(); } catch {}
      onUpdated && onUpdated();
    } catch (e) {
      message.error(e.message || '保存失败');
    }
  };

  const onTwoFaFinish = async (values) => {
    const url = (values.two_fa_url && String(values.two_fa_url).trim()) || DEFAULT_TWO_FA_URL;
    setSavingTwoFa(true);
    try {
      await updateTwoFaSettings({ two_fa_url: url });
      setTwoFaUrlCache(url);
      twoFaForm.setFieldsValue({ two_fa_url: url });
      message.success('2FA 接码网址已保存');
    } catch (e) {
      message.error(e.message || '保存失败');
    } finally {
      setSavingTwoFa(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="2FA 接码网址">
        <Form form={twoFaForm} layout="vertical" onFinish={onTwoFaFinish} initialValues={{ two_fa_url: DEFAULT_TWO_FA_URL }}>
          <Form.Item
            name="two_fa_url"
            label="接码网址"
            extra="复制账号信息且填写了 2FA 时，会附带此链接。留空则恢复默认地址。"
            rules={[
              {
                validator: (_, value) => {
                  const v = value == null ? '' : String(value).trim();
                  if (!v) return Promise.resolve();
                  if (!/^https?:\/\//i.test(v)) {
                    return Promise.reject(new Error('请输入以 http:// 或 https:// 开头的网址'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input placeholder={DEFAULT_TWO_FA_URL} allowClear />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={savingTwoFa}>
                保存
              </Button>
              <Button
                onClick={() => {
                  twoFaForm.setFieldsValue({ two_fa_url: DEFAULT_TWO_FA_URL });
                }}
              >
                恢复默认
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card title="修改账号密码">
        <Form form={authForm} layout="vertical" onFinish={onAuthFinish}>
          <Form.Item name="username" label="管理员账号" rules={[{ required: true, message: '请输入账号' }]}>
            <Input placeholder="输入新的管理员账号" />
          </Form.Item>
          <Form.Item name="currentPassword" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password placeholder="当前密码" />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={[{ required: true, message: '请输入新密码' }]}>
            <Input.Password placeholder="新密码" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="确认新密码" dependencies={['newPassword']} rules={[{ required: true, message: '请确认新密码' }]}>
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">保存并退出登录</Button>
          </Form.Item>
        </Form>
      </Card>
      </Space>
    </div>
  );
};

export default SystemSettings;
